/**
 * SAM3 (Segment Anything Model 3) API client via fal.ai.
 * Endpoint: fal-ai/sam-3/image
 *
 * Supports text-prompt segmentation — given a flat product image
 * and a text prompt (e.g. "hoodie hand pockets"), returns mask images.
 */

import { resolveFalImageInputUrl } from "./specsImageUpload";

export interface Sam3Result {
  /** Primary mask preview (mask overlaid on image) */
  previewUrl: string | null;
  /** Individual mask image URLs */
  masks: { url: string; width: number; height: number }[];
  /** Confidence scores per mask (if requested) */
  scores?: number[];
}

export async function callSam3(
  imageUrl: string,
  prompt: string,
  falApiKey: string,
  options: {
    applyMask: boolean;
  }
): Promise<Sam3Result> {
  if (!falApiKey) throw new Error("fal.ai API key is required");

  const applyMask = options.applyMask;
  const maskModel = applyMask ? "sam-3/image:applied-mask" : "sam-3/image:mask";

  // Check cache
  const { getCached, setCached } = await import("./falCache");
  const cached = await getCached(imageUrl, prompt, maskModel);
  if (cached) {
    return {
      previewUrl: null,
      masks: [{ url: cached, width: 0, height: 0 }],
      scores: [1],
    };
  }

  const falImageUrl = await resolveFalImageInputUrl(imageUrl);

  const response = await fetch("https://fal.run/fal-ai/sam-3/image", {
    method: "POST",
    headers: {
      Authorization: `Key ${falApiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      image_url: falImageUrl,
      prompt,
      apply_mask: applyMask,
      output_format: "png",
      sync_mode: true,
      return_multiple_masks: false,
      max_masks: 1,
      include_scores: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`SAM3 error ${response.status}: ${await response.text()}`);
  }

  const data = await response.json();

  const result: Sam3Result = {
    previewUrl: data.image?.url ?? null,
    masks: (data.masks ?? []).map(
      (m: { url: string; width?: number; height?: number }) => ({
        url: m.url,
        width: m.width ?? 0,
        height: m.height ?? 0,
      })
    ),
    scores: data.scores,
  };

  // Cache the first mask URL
  const firstMaskUrl = result.masks[0]?.url;
  if (firstMaskUrl) {
    setCached(imageUrl, prompt, maskModel, firstMaskUrl);
  }

  return result;
}
